import type { Metro } from '../../types/metro';
import { formatRatio } from '../../lib/utils';
import { RatioMethodologyNote } from './RatioMethodologyNote';

export interface NationalReferenceStatProps {
  /**
   * The loaded metro list the national figure is computed from. The
   * parent (RankingsTable) only renders this component once metros have
   * loaded, so an empty array is a defensive edge, not a loading state.
   */
  readonly metros: readonly Metro[];
}

/**
 * National reference banner rendered above the rankings table.
 *
 * Combines two previously separate elements into one horizontal bar:
 *   - Left:  eyebrow label + national ratio value + scope/shortage description
 *   - Right: "How the ratio is computed" methodology pill (tooltip)
 *
 * The two used to render as stacked blocks (the National Median card
 * above the methodology aside), which ate vertical real estate and
 * made the table feel further down the page. Merging them tells one
 * coherent story left → right: scope of the number, then the affordance
 * for how it was computed.
 *
 * Responsive behavior:
 *   - >= md: single row, descriptive text inline with label+value,
 *     pill anchored to the right via `md:ml-auto`.
 *   - <  md: descriptive text wraps under the value; the pill flows
 *     onto its own line so it never gets squeezed against the text.
 *
 * The value is the openings-weighted national average: total credentials
 * across all 55 metros over total job openings. This equals the
 * job-openings-weighted mean of the per-metro ratios (Georgetown's
 * methodology) and agrees with the homepage's "~30% of demand met" stat.
 * Until 2026-07-23 this was a hardcoded `0.87` left over from the dummy
 * dataset — wrong against the production data (real value ≈ 0.29).
 */
export function NationalReferenceStat({ metros }: NationalReferenceStatProps) {
  const totalCredentials = metros.reduce((sum, m) => sum + m.totalCredentials, 0);
  const totalJobs = metros.reduce((sum, m) => sum + m.totalJobs, 0);
  const nationalRatio = totalJobs > 0 ? formatRatio(totalCredentials / totalJobs) : '—';
  return (
    <div
      // 2026-07-02 (V2 feedback pass): reviewers wanted the reference
      // banner to say "National average" not "National median" — the
      // Georgetown methodology weights by metro job openings, which
      // produces an average, not a positional median.
      aria-label="National average reference"
      className="mb-6 flex w-full flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-border bg-ice px-5 py-3 shadow-card md:flex-nowrap"
    >
      <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-text-3">
          National average
        </span>
        <span className="text-[1.15rem] font-bold tabular-nums text-navy">{nationalRatio}</span>
        <span className="text-[13px] leading-[1.5] text-text-3">
          {/* 2026-07-02 (V2 feedback): "at or above 1.0 indicates
              surplus" -> "above 1.0 indicates a surplus" — reviewers
              wanted the parity point (exactly 1.0) called out
              separately so the surplus band is unambiguously > 1.0. */}
          Credential-to-job ratio across all 55 metros &middot; Below 1.0 indicates shortage; above 1.0 indicates a surplus
        </span>
      </div>
      <div className="ml-auto flex-shrink-0">
        <RatioMethodologyNote />
      </div>
    </div>
  );
}
